import {
  type NodeEntry,
  type BaseEditor,
  type Editor,
  type EditorNormalizeOptions,
  Element,
} from 'slate';
import type {
  MetadataNormalizationPlugin,
  NormalizationPlugin,
  SchemaPlugin,
} from './types/aha-editor';

import NeverEmptyPlugin from './plugins/NeverEmptyPlugin/NeverEmptyPlugin';
import ListNormalizationsPlugin from './plugins/ListPlugin/ListNormalizationsPlugin';
import LinkNormalizationPlugin from './plugins/LinkPlugin/LinkNormalization';
import CodeBlockNormalizationsPlugin from './plugins/CodeBlockPlugin/CodeBlockNormalizationsPlugin';
import TableNormalizationPlugin from './plugins/TablePlugin/TableNormalization';
import AnnotationsNormalizationPlugin from './plugins/MetadataNormalizationPlugin/AnnotationsNormalizationPlugin';
import TableOfContentsEnabledNormalizationPlugin from './plugins/MetadataNormalizationPlugin/TableOfContentsNormalization';
import HideInlinePlugin from './plugins/HideInlinePlugin/HideInlinePlugin';
import ReferenceUserSchemaPlugin from './plugins/ReferencesPlugin/ReferenceUserSchemaPlugin';
import ReferenceRecordSchemaPlugin from './plugins/ReferencesPlugin/ReferenceRecordSchemaPlugin';
import ReferenceEmailVariableSchemaPlugin from './plugins/ReferencesPlugin/ReferenceEmailVariableSchemaPlugin';
import {
  SharedSnippetBlockSchemaPlugin,
  SharedSnippetInlineSchemaPlugin,
} from './plugins/SharedSnippetPlugin/SharedSnippetSchemaPlugin';
import {
  AttachmentSchemaPlugin,
  AudioSchemaPlugin,
  ImageSchemaPlugin,
  VideoSchemaPlugin,
} from './plugins/MediaPlugin/MediaSchemaPlugin';
import CodeSnippetSchemaPlugin from './plugins/Formatting/CodeSnippetPlugin/CodeSnippetSchemaPlugin';
import DividerLineSchemaPlugin from './plugins/DividerLinePlugin/DividerLineSchemaPlugin';
import LinkSchemaPlugin from './plugins/LinkPlugin/LinkSchemaPlugin';
import LabelPillSchemaPlugin from './plugins/LabelPillPlugin/LabelPillSchemaPlugin';
import EmbedSchemaPlugin from './plugins/EmbedPlugin/EmbedSchemaPlugin';
import TranscriptCueSchemaPlugin from './plugins/TranscriptPlugin/TranscriptCueSchemaPlugin';
import EmbeddedReportSchemaPlugin from './plugins/EmbeddedReportPlugin/EmbeddedReportSchemaPlugin';
import EmbeddedWhiteboardSchemaPlugin from './plugins/EmbeddedWhiteboardPlugin/EmbeddedWhiteboardSchemaPlugin';

const allNormalizations: NormalizationPlugin[] = [
  NeverEmptyPlugin.normalization!,
  ListNormalizationsPlugin.normalizations!,
  LinkNormalizationPlugin.normalization!,
  CodeBlockNormalizationsPlugin.map((p) => p.normalizations!),
  TableNormalizationPlugin.normalizations!,
].flat(10);

const allMetadataNormalizations: MetadataNormalizationPlugin[] = [
  AnnotationsNormalizationPlugin.metadataNormalizations!,
  TableOfContentsEnabledNormalizationPlugin.metadataNormalizations!,
].flat(10);

const initialSchemaValue: {
  inlineElementTypes: string[];
  voidElementTypes: string[];
} = { inlineElementTypes: [], voidElementTypes: [] };

const allSchemas = [
  HideInlinePlugin.schema,
  ReferenceUserSchemaPlugin.schema,
  ReferenceRecordSchemaPlugin.schema,
  ReferenceEmailVariableSchemaPlugin.schema,
  SharedSnippetBlockSchemaPlugin.schema,
  SharedSnippetInlineSchemaPlugin.schema,
  ImageSchemaPlugin.schema,
  VideoSchemaPlugin.schema,
  AttachmentSchemaPlugin.schema,
  AudioSchemaPlugin.schema,
  CodeSnippetSchemaPlugin.schema,
  DividerLineSchemaPlugin.schema,
  TranscriptCueSchemaPlugin.schema,
  LinkSchemaPlugin.schema,
  LabelPillSchemaPlugin.schema,
  EmbedSchemaPlugin.schema,
  EmbeddedReportSchemaPlugin.schema,
  EmbeddedWhiteboardSchemaPlugin.schema,
].reduce((acc, schema: SchemaPlugin) => {
  for (const [elementType, schemaDefinition] of Object.entries(schema)) {
    if (schemaDefinition.isInline) {
      acc.inlineElementTypes.push(elementType);
    }

    if (schemaDefinition.isVoid) {
      acc.voidElementTypes.push(elementType);
    }
  }
  return acc;
}, initialSchemaValue);

const shouldApply = (
  editor: Editor,
  normalization: NormalizationPlugin,
  entry: NodeEntry
) => {
  const [node] = entry;
  if (normalization.check) {
    return normalization.check(editor, entry);
  }

  const { type } = normalization;
  if (Array.isArray(normalization.type)) {
    return Element.isElement(node) && type.includes(node.type);
  }

  return Element.isElement(node) && type === node.type;
};

export default class Normalization {
  static initializeNormalization(
    editor: Editor,
    normalizations = allNormalizations,
    metadataNormalizations = allMetadataNormalizations,
    schema = allSchemas
  ) {
    const { normalizeNode, normalize, isInline, isVoid } = editor;
    editor.normalizeNode = (entry: NodeEntry) => {
      const result = this.normalizeNode(
        editor,
        entry,
        normalizations,
        normalizeNode
      );

      if (!result) {
        normalizeNode(entry);
      }
    };

    editor.normalize = (options: EditorNormalizeOptions | undefined = {}) => {
      this.normalizeMetadata(editor, metadataNormalizations);
      normalize(options);
    };

    editor.isInline = (element) => {
      if (schema.inlineElementTypes.includes(element.type)) {
        return true;
      } else {
        return isInline(element);
      }
    };

    editor.isVoid = (element) => {
      if (schema.voidElementTypes.includes(element.type)) {
        return true;
      } else {
        return isVoid(element);
      }
    };
  }

  static normalizeNode(
    editor: Editor,
    entry: NodeEntry,
    normalizations: NormalizationPlugin[],
    normalizeNode: BaseEditor['normalizeNode']
  ) {
    const [node] = entry;
    // We only want to run our normalizations on valid nodes,
    // a node with an empty children array is invalid so we leave
    // it to the default normalization to handle
    if (Element.isElement(node) && node.children.length === 0) {
      normalizeNode(entry);
    }

    for (const normalization of normalizations) {
      if (!shouldApply(editor, normalization, entry)) continue;

      const result = normalization.run(editor, entry);
      if (result) return result;
    }
  }

  static normalizeMetadata(
    editor: Editor,
    normalizations: MetadataNormalizationPlugin[]
  ) {
    for (const normalization of normalizations) {
      const result = normalization.run(editor);
      if (result) return result;
    }
  }
}
